import React, { useEffect, useState } from "react";
import { Smartphone, Share, Plus, X, Download } from "lucide-react";

/**
 * PWA install UX for RD SmartMetrics.
 *
 * Renders TWO surfaces:
 *  1. `<InstallButton />` — a small pill in the top nav that fires the
 *     browser's install prompt on Android/desktop Chromium and opens an
 *     iOS "Add to Home Screen" instructions modal on iPhone/iPad.
 *  2. `<InstallBanner />` — a slim first-login banner ("Install RD
 *     SmartMetrics for faster access") with Install + Not Now buttons.
 *     Dismissed via `sessionStorage` so it stays hidden for the rest of
 *     the session but re-appears next login (per user choice).
 *
 * The whole surface auto-hides when:
 *   - App is already running installed (display-mode: standalone, or
 *     iOS `navigator.standalone`), OR
 *   - No install prompt is available AND we're not on iOS (nothing to do).
 */

const SESSION_DISMISS_KEY = "rd_install_banner_dismissed_session";

function isStandalone() {
  if (typeof window === "undefined") return false;
  const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone = "standalone" in window.navigator && window.navigator.standalone === true;
  return Boolean(mm || iosStandalone);
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Includes iPad on iOS 13+ which UA-spoofs as MacIntel with touch.
  const iPadOS = navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  return /iPad|iPhone|iPod/.test(ua) || iPadOS;
}

/** Shared hook: exposes the deferred prompt event + install() trigger. */
function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    if (installed) return;

    const onBeforeInstall = (e) => {
      // Chromium fires this once its heuristics allow install; stash it
      // so we can call prompt() on-demand from our own button.
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)");
    const onDisplayChange = () => setInstalled(isStandalone());

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    if (mm && mm.addEventListener) mm.addEventListener("change", onDisplayChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (mm && mm.removeEventListener) mm.removeEventListener("change", onDisplayChange);
    };
  }, [installed]);

  const promptInstall = async () => {
    if (!deferred) return "unavailable";
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      return choice?.outcome === "accepted" ? "accepted" : "dismissed";
    } catch {
      return "error";
    }
  };

  return {
    installed,
    canPrompt: !!deferred,
    isIOS: isIOS(),
    promptInstall,
  };
}

/** Small pill in the top nav. Hidden when the app is already installed
 *  and when there's no prompt available on non-iOS (nothing to offer). */
export function InstallButton() {
  const { installed, canPrompt, isIOS: ios, promptInstall } = useInstallPrompt();
  const [showIOS, setShowIOS] = useState(false);

  if (installed) return null;
  if (!canPrompt && !ios) return null;

  const onClick = async () => {
    if (canPrompt) {
      await promptInstall();
      return;
    }
    if (ios) setShowIOS(true);
  };

  return (
    <>
      <button
        onClick={onClick}
        data-testid="install-app-btn"
        title="Install RD SmartMetrics"
        className="hidden sm:flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-lg border border-[#7C3AED]/40 bg-[#7C3AED]/10 hover:bg-[#7C3AED]/20 text-[#A78BFA] hover:text-white transition-colors"
      >
        <Smartphone size={13} />
        Install App
      </button>
      {/* Mobile-only compact icon variant (saves space in the top bar) */}
      <button
        onClick={onClick}
        data-testid="install-app-btn-mobile"
        aria-label="Install RD SmartMetrics"
        className="sm:hidden w-10 h-10 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/40 flex items-center justify-center text-[#A78BFA] hover:bg-[#7C3AED]/20 hover:text-white"
      >
        <Smartphone size={15} />
      </button>
      {showIOS && <IOSInstallModal onClose={() => setShowIOS(false)} />}
    </>
  );
}

/** First-login banner. Dismissed to sessionStorage so it reappears on next login. */
export function InstallBanner() {
  const { installed, canPrompt, isIOS: ios, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [showIOS, setShowIOS] = useState(false);

  if (installed || dismissed) return null;
  if (!canPrompt && !ios) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const install = async () => {
    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome === "accepted" || outcome === "dismissed") dismiss();
      return;
    }
    if (ios) setShowIOS(true);
  };

  return (
    <>
      <div
        data-testid="install-app-banner"
        className="mx-3 md:mx-6 mt-3 flex items-center gap-3 bg-gradient-to-r from-[#7C3AED]/15 to-[#5B21B6]/10 border border-[#7C3AED]/40 rounded-xl px-4 py-2.5"
      >
        <div className="w-9 h-9 rounded-lg bg-[#7C3AED]/20 border border-[#7C3AED]/40 flex items-center justify-center text-[#A78BFA] flex-shrink-0">
          <Smartphone size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white font-semibold leading-tight">Install RD SmartMetrics for faster access.</div>
          <div className="text-[11px] text-[#A1A1AA] mt-0.5 hidden sm:block">
            {ios && !canPrompt
              ? "On iPhone/iPad: Share → Add to Home Screen."
              : "Opens in its own window, no browser bar."}
          </div>
        </div>
        <button
          onClick={install}
          data-testid="install-app-banner-install"
          className="text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white flex items-center gap-1.5"
        >
          <Download size={12} />
          Install App
        </button>
        <button
          onClick={dismiss}
          data-testid="install-app-banner-dismiss"
          className="text-xs uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-lg border border-white/15 hover:border-white/30 text-[#A1A1AA] hover:text-white"
        >
          Not Now
        </button>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="sm:hidden text-[#71717A] hover:text-white p-1"
        >
          <X size={16} />
        </button>
      </div>
      {showIOS && <IOSInstallModal onClose={() => setShowIOS(false)} />}
    </>
  );
}

/** Instructions overlay for iOS Safari (which does not fire beforeinstallprompt). */
function IOSInstallModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="ios-install-modal"
    >
      <div
        className="w-full sm:max-w-sm bg-[#121214] border border-white/10 rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Smartphone size={16} className="text-[#A78BFA]" />
            <div className="text-sm font-bold text-white">Install on iPhone / iPad</div>
          </div>
          <button
            onClick={onClose}
            data-testid="ios-install-close"
            className="p-1 text-[#71717A] hover:text-white"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <ol className="p-4 space-y-3 text-sm text-[#E4E4E7]">
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-[#7C3AED]/20 border border-[#7C3AED]/40 text-[#A78BFA] text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
            <div>
              Tap the <b className="inline-flex items-center gap-1"><Share size={12} className="inline text-[#A78BFA]" />Share</b> icon at the bottom (or top) of Safari.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-[#7C3AED]/20 border border-[#7C3AED]/40 text-[#A78BFA] text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <div>
              Scroll and tap <b className="inline-flex items-center gap-1"><Plus size={12} className="inline text-[#A78BFA]" />Add to Home Screen</b>.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-[#7C3AED]/20 border border-[#7C3AED]/40 text-[#A78BFA] text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
            <div>
              Tap <b className="text-white">Add</b> — RD SmartMetrics appears on your home screen and opens like a regular app.
            </div>
          </li>
        </ol>
        <div className="px-4 py-3 border-t border-white/10 flex justify-end">
          <button
            onClick={onClose}
            data-testid="ios-install-got-it"
            className="text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
